import vtkDistance2DWidgetFactory from "./Distance2DWidget";
import { bindSVGRepresentation } from '../js/SVGHelpers';
import { useEffect, useRef } from "react";
import { Vector3 } from "../js/Vector3";
import vtkInteractorObserver from 'vtk.js/Sources/Rendering/Core/InteractorObserver';
import { getNumberOfPlacedHandles } from 'vtk.js/Sources/Widgets/Widgets3D/LineWidget/helpers';
const { computeWorldToDisplay } = vtkInteractorObserver;
import { logDebug, debugCallback, isNullOrUndefined, WidgetType, getDUName, SlicingModeName, LengthMeasurementStateName } from '../js/Common';

const isDebugging = false;

function log(msg, ...params) {
    logDebug("DicomVisualization.Distance", isDebugging, msg, ...params);
}

function debug(callback) {
    debugCallback(isDebugging, callback);
}

function isValidPoint(point) {
    return Array.isArray(point) && point.length === 2 && !point.some(Number.isNaN);
}

function calculatePerpendicularHandleCoordinates(center, lineStart, lineEnd) {
    if (!isValidPoint(center) || !isValidPoint(lineStart) || !isValidPoint(lineEnd)) {
        return null;
    }
    const tickLength = 16;
    const halfTickLength = tickLength / 2;
    const dx = lineEnd[0] - lineStart[0];
    const dy = lineEnd[1] - lineStart[1];
    const length = Math.hypot(dx, dy);

    if (length === 0) {
        return {
            x1: center[0],
            y1: center[1] - halfTickLength,
            x2: center[0],
            y2: center[1] + halfTickLength
        };
    }

    const px = -dy / length;
    const py = dx / length;

    return {
        x1: center[0] - px * halfTickLength,
        y1: center[1] - py * halfTickLength,
        x2: center[0] + px * halfTickLength,
        y2: center[1] + py * halfTickLength
    };
}

function setupSVG(w, renderer, genericRenderWindow) {
    return(
        bindSVGRepresentation(renderer, w.getWidgetState(), genericRenderWindow, {
            mapState(widgetState, { renderWindowSize }) {
                if (widgetState.isDeleted() || !w.getPickable() || isNullOrUndefined(renderer) || renderer.isDeleted()) { return null; }
                const textState = widgetState.getText();
                const text = textState.getText();
                const origin = textState.getOrigin();
                const handle1Origin = widgetState.getHandle1().getOrigin();
                const handle2Origin = widgetState.getHandle2().getOrigin();
                const moveHandleOrigin = widgetState.getMoveHandle()?.getOrigin();
                let moveHandlePosition = null;
                debug(() => global.debugW = w);
                if (moveHandleOrigin) {
                    const moveHandleCoords = computeWorldToDisplay(renderer, ...moveHandleOrigin);
                    moveHandlePosition = [moveHandleCoords[0], renderWindowSize[1] - moveHandleCoords[1]];
                }
                if (origin && handle1Origin && handle2Origin) {
                    const coords = computeWorldToDisplay(renderer, ...origin);
                    const position = [coords[0], renderWindowSize[1] - coords[1]];
                    const firstCoords = computeWorldToDisplay(renderer, ...handle1Origin);
                    const firstPosition = [firstCoords[0], renderWindowSize[1] - firstCoords[1]];
                    const secondCoords = computeWorldToDisplay(renderer, ...handle2Origin);
                    const secondPosition = [secondCoords[0], renderWindowSize[1] - secondCoords[1]];
                    return {
                        text,
                        position,
                        firstPosition,
                        secondPosition,
                        moveHandlePosition
                    };
                }
                return null;
            },
            render(data, h) {
                if (!data) { return []; }

                const nodes = [];
                const positionValid = isValidPoint(data.position);
                const firstValid = isValidPoint(data.firstPosition);
                const secondValid = isValidPoint(data.secondPosition);
                const moveValid = isValidPoint(data.moveHandlePosition);

                const lineStart = firstValid ? data.firstPosition : null;
                const lineEnd = moveValid ? data.moveHandlePosition : (secondValid ? data.secondPosition : null);

                if (positionValid) {
                    let x = data.position[0];
                    let y = data.position[1];
                    if (firstValid && data.firstPosition[0] > x) {
                        x = data.firstPosition[0];
                        y = data.firstPosition[1];
                    }
                    if (secondValid && data.secondPosition[0] > x) {
                        x = data.secondPosition[0];
                        y = data.secondPosition[1];
                    }

                    const distanceText =
                        h(
                            'text',
                            {
                                key: 'lineText',
                                attrs: {
                                    x: x,
                                    y: y,
                                    dx: 6,
                                    dy: -12,
                                    fill: 'yellow',
                                    'font-size': 15
                                },
                            },
                            data.text,
                        );
                    nodes.push(distanceText);
                }
                if ((firstValid && secondValid) || (firstValid && moveValid)) {
                    const line =
                        h(
                            'line',
                            {
                                key: 'line',
                                attrs: {
                                    x1: data.firstPosition[0],
                                    x2: moveValid ? data.moveHandlePosition[0] : data.secondPosition[0],
                                    y1: data.firstPosition[1],
                                    y2: moveValid ? data.moveHandlePosition[1] : data.secondPosition[1],
                                    stroke: 'red',
                                    'stroke-width': 2
                                }
                            },
                        );
                    nodes.push(line);
                }
                
                const firstTick = calculatePerpendicularHandleCoordinates(data.firstPosition, lineStart, lineEnd);
                if (firstTick) {
                    nodes.push(
                        h('line', {
                            key: 'handle1Tick',
                            attrs: {
                                ...firstTick,
                                stroke: 'yellow',
                                'stroke-width': 3
                            }
                        })
                    );
                }
                if (moveValid) {
                    const moveTickCoords = calculatePerpendicularHandleCoordinates(data.moveHandlePosition, lineStart, lineEnd);
                    if (moveTickCoords) {
                        nodes.push(
                            h('line', {
                                key: 'moveHandleTick',
                                attrs: {
                                    ...moveTickCoords,
                                    stroke: 'yellow',
                                    'stroke-width': 3
                                }
                            })
                        );
                    }
                } else if (secondValid) {
                    const secondTickCoords = calculatePerpendicularHandleCoordinates(data.secondPosition, lineStart, lineEnd);
                    if (secondTickCoords) {
                        nodes.push(
                            h('line', {
                                key: 'handle2Tick',
                                attrs: {
                                    ...secondTickCoords,
                                    stroke: 'yellow',
                                    'stroke-width': 3
                                }
                            })
                        );
                    }
                }
                return nodes;
            },
        }));
}

function mkDistanceWidget(renderer, addedMeasurementCallback) {
    const widget = vtkDistance2DWidgetFactory.newInstance(addedMeasurementCallback);
    const camera = renderer.getActiveCamera();
    widget.getManipulator().setUserOrigin(camera.getFocalPoint());

    return widget;
}

const removeWidget = (distanceId, distanceWidgets, widgetManager, removedMeasurementCallback) => {
    const widgetInfo = distanceWidgets.get(distanceId);
    if (!widgetInfo) { return; }

    const placedHandles = getNumberOfPlacedHandles(widgetInfo.widget.getWidgetState());
    log(`Removing distance widget ${distanceId} (placed handles: ${placedHandles})`);
    widgetManager.removeWidget(widgetInfo.widget);
    widgetInfo.svgCleanupCallback?.();
    distanceWidgets.delete(distanceId);
    if (placedHandles > 0) {
        removedMeasurementCallback(distanceId);
    }
    log(`Widgets in map: ${[...distanceWidgets.keys()]}`);
    log(`Widgets in manager: ${widgetManager.getWidgets().length}`);
};

function addRemoveOnClick(distanceId, distanceWidgets, widgetManager, removedMeasurementCallback, getRemovingMeasurement) {
    const widgetInfo = distanceWidgets.get(distanceId);
    if (!widgetInfo || widgetInfo.handle.isDeleted()) { return; }

    const removeOnClick = () => {
        if (!getRemovingMeasurement()) { return; }
        removeWidget(distanceId, distanceWidgets, widgetManager, removedMeasurementCallback);
    };

    widgetInfo.handle.onEndInteractionEvent(removeOnClick);
}

function removeWidgets(widgetManager, distanceWidgets, removedMeasurementCallback) {
    for (const [distanceId, _] of distanceWidgets) {
        removeWidget(distanceId, distanceWidgets, widgetManager, removedMeasurementCallback);
    }
}

export function useDistanceWidget(parentContext, props, slicePlaneIndex, activeWidget, slicingMode) {
    const {
        measurementIds = [],
        lengthMeasurementState = null,
        measurementPlacementOwner = null,
        placementStartedCallback = () => {},
        addedMeasurementCallback = () => {},
        removedMeasurementCallback = () => {},
    } = props ?? {};
    const context = useRef(null);
    const parentContextRef = useRef(parentContext);
    const lengthMeasurementStateRef = useRef(null);

    useEffect(() => {
        if (parentContext) {

            if (!context.current) {
                const distanceWidgets = new Map();
                context.current = { distanceWidgets };
            } else if (parentContext.renderer && parentContext.mapper &&
                       (getDUName(slicingMode) === SlicingModeName.AXIAL ||
                        getDUName(slicingMode) === SlicingModeName.CORONAL ||
                        getDUName(slicingMode) === SlicingModeName.SAGITTAL)) {
                const { widgetManager, mapper, renderer } = parentContext;
                const { distanceWidgets } = context.current;
                debug(() => global.debugMapper = mapper);
                debug(() => global.debugWidgetManager = widgetManager);
                debug(() => global.debugDistanceWidgets = distanceWidgets);
                debug(() => global.debugGenericRenderWindow = parentContext.genericRenderWindow);
                const currentSlice = mapper.getSlice();
                log(`Current slice: ${currentSlice} vs ${slicePlaneIndex}`);

                if (measurementIds && measurementIds.length > 0) {
                    measurementIds.forEach((distanceId) => {
                        if (getDUName(activeWidget) === WidgetType.DISTANCE_WIDGET
                            && !distanceWidgets.has(distanceId) && getDUName(lengthMeasurementState) === LengthMeasurementStateName.ADDING) {
                            log(`Adding distance ${distanceId} at slice ${slicePlaneIndex}`);

                            const distanceWidget = mkDistanceWidget(renderer, addedMeasurementCallback);
                            const behaviourHandle = widgetManager.addWidget(distanceWidget);

                            widgetManager.grabFocus(distanceWidget);

                            debug(() => global.debugDistanceWidget = distanceWidget);
                            debug(() => global.debugHandle = behaviourHandle);

                            const { cleanupCallbacks, updateVNode } =
                                setupSVG(behaviourHandle, renderer, parentContext.genericRenderWindow);

                            distanceWidgets.set(
                                distanceId,
                                {
                                    sliceIndex: slicePlaneIndex,
                                    handle: behaviourHandle,
                                    widget: distanceWidget,
                                    svgCleanupCallback: cleanupCallbacks,
                                    updateVNode: updateVNode
                                });

                            behaviourHandle.getWidgetState().getPositionOnLine().setPosOnLine(0.3);

                            //turn off the handle visibility
                            behaviourHandle.setHandleVisibility(false);

                            behaviourHandle.onInteractionEvent(() => {
                                const distance = distanceWidgets.get(distanceId).widget.getDistance();
                                behaviourHandle.setText(`${distance.toFixed(2)} mm`);
                            });

                            let firstHandleReported = false;
                            behaviourHandle.onStartInteractionEvent(() => {
                                if (!firstHandleReported
                                    && getNumberOfPlacedHandles(behaviourHandle.getWidgetState()) >= 1) {
                                    firstHandleReported = true;
                                    placementStartedCallback();
                                }
                            });

                            lengthMeasurementStateRef.current = getDUName(lengthMeasurementState);
                            const getRemovingMeasurement = () => lengthMeasurementStateRef.current === LengthMeasurementStateName.REMOVING;
                            addRemoveOnClick(distanceId, distanceWidgets, widgetManager, removedMeasurementCallback, getRemovingMeasurement);
                        } else if (distanceWidgets.has(distanceId)) {
                            const widgetInfo = distanceWidgets.get(distanceId);

                            const placedHere = getNumberOfPlacedHandles(widgetInfo.widget.getWidgetState());
                            const lockedAfterComplete = getDUName(lengthMeasurementState) !== LengthMeasurementStateName.ADDING && placedHere === 0;
                            const lockedDuringPlacement = measurementPlacementOwner != null && measurementPlacementOwner !== getDUName(slicingMode) && placedHere === 0;
                            const locked = lockedAfterComplete || lockedDuringPlacement;

                            // VRM-4370: Fix this, it was never actually working properly and now that we are properly removing widgets it's extra broken
                            //if (getDUName(lengthMeasurementState) === LengthMeasurementStateName.IDLE
                            //    && getNumberOfPlacedHandles(widgetInfo.widget.getWidgetState()) !== 2) {
                            //    removeWidget(distanceId, distanceWidgets, widgetManager, removedMeasurementCallback);
                            //}

                            lengthMeasurementStateRef.current = getDUName(lengthMeasurementState);

                            debug(() => global.debugInfo = widgetInfo);
                            if (locked) {
                                if (!widgetInfo.handle.isDeleted()) {
                                    if (widgetInfo.handle.getPickable()) {
                                        widgetInfo.handle.setPickable(false);
                                        widgetInfo.updateVNode();
                                    }
                                    if (widgetInfo.handle.hasFocus?.()) {
                                        widgetInfo.handle.loseFocus();
                                    }
                                }
                            } else if (!widgetInfo.handle.isDeleted() && !isNullOrUndefined(widgetInfo.sliceIndex) && widgetInfo.sliceIndex.toFixed(0) !== slicePlaneIndex.toFixed(0)) {
                                if (widgetInfo.handle.getPickable()) {
                                    log(`Widget on wrong slice, turn off visibility ${widgetInfo.sliceIndex} vs ${slicePlaneIndex}`);
                                    widgetInfo.handle.setPickable(false);
                                    widgetInfo.updateVNode();
                                }
                            } else if (!widgetInfo.handle.isDeleted()) {
                                if (!widgetInfo.handle.getPickable()) {
                                    log(`Widget ${distanceId} on correct slice, turn on visibility ${widgetInfo.sliceIndex} vs ${slicePlaneIndex}`);
                                    widgetInfo.handle.setPickable(true);
                                    widgetInfo.updateVNode();
                                }
                            }
                        }
                    });

                    widgetManager.enablePicking();

                    for (const widgetId of [...distanceWidgets.keys()]) {
                        if (!measurementIds.includes(widgetId)) {
                            removeWidget(widgetId, distanceWidgets, widgetManager, removedMeasurementCallback);
                        }
                    }
                } else if (distanceWidgets.size > 0) {
                    log(`No distance ids provided, clearing distance widgets`);
                    removeWidgets(widgetManager, distanceWidgets, removedMeasurementCallback);
                    widgetManager.disablePicking();
                }
            }
        }
    }, [parentContext, measurementIds, lengthMeasurementState, measurementPlacementOwner, slicePlaneIndex, activeWidget, slicingMode, addedMeasurementCallback, removedMeasurementCallback]);

    useEffect(() => {
        parentContextRef.current = parentContext;
    }, [parentContext]);

    useEffect(() => {
        log('Mounting');
        return () => {
            if (parentContextRef.current && context.current) {
                log(`Disposing distance widgets with parentContext`);
                const { widgetManager } = parentContextRef.current;
                const { distanceWidgets } = context.current;
                if (widgetManager && !widgetManager.isDeleted()) {
                    removeWidgets(widgetManager, distanceWidgets);
                }
                distanceWidgets.clear();
                context.current = { distanceWidgets };

            } else if (context.current) {
                log(`Disposing distance widgets without parentContext`);
                const { distanceWidgets } = context.current;
                distanceWidgets.forEach((widget) => {
                    widget.svgCleanupCallback();
                });
                distanceWidgets.clear();
                context.current = { distanceWidgets };
            }
        };
    }, []);
}